import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  FlaskConical, ArrowLeft, Trophy, Star, 
  RotateCcw, Beaker, Atom, Sparkles, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ScienceLabGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [currentExperiment, setCurrentExperiment] = useState(0);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(0);
  const [completedExperiments, setCompletedExperiments] = useState(0);

  const experiments = [
    {
      id: 1,
      name: 'Acid-Base Reaction',
      question: 'What happens when you mix vinegar (acid) with baking soda (base)?',
      ingredients: ['Vinegar', 'Baking Soda', 'Water', 'Salt'],
      correctPair: ['Vinegar', 'Baking Soda'],
      result: 'Carbon dioxide gas bubbles form! This is called effervescence.',
      wrongResult: 'Nothing happens. These substances don\'t react with each other.',
      explanation: 'When an acid meets a base, they neutralize each other and release CO₂ gas!',
      points: 25
    },
    {
      id: 2,
      name: 'Rust Formation',
      question: 'Which two things are needed for iron to rust?',
      ingredients: ['Iron', 'Oxygen', 'Gold', 'Nitrogen'],
      correctPair: ['Iron', 'Oxygen'],
      result: 'Iron oxide (rust) forms! The iron turns orange-brown.',
      wrongResult: 'No reaction occurs. These elements don\'t form rust together.',
      explanation: 'Rust is iron oxide - it forms when iron reacts with oxygen in the presence of water.',
      points: 25
    },
    {
      id: 3,
      name: 'Photosynthesis',
      question: 'What do plants need to make glucose through photosynthesis?',
      ingredients: ['Carbon Dioxide', 'Sunlight', 'Darkness', 'Nitrogen'],
      correctPair: ['Carbon Dioxide', 'Sunlight'],
      result: 'The plant produces glucose and releases oxygen!',
      wrongResult: 'Photosynthesis cannot occur with these inputs.',
      explanation: 'Plants use CO₂ + H₂O + sunlight → Glucose + O₂. This is how they make food!',
      points: 25
    },
    {
      id: 4,
      name: 'Electrolysis of Water',
      question: 'What gases are produced when electricity passes through water?',
      ingredients: ['Hydrogen', 'Oxygen', 'Nitrogen', 'Helium'],
      correctPair: ['Hydrogen', 'Oxygen'],
      result: 'Bubbles of hydrogen and oxygen gas appear at the electrodes!',
      wrongResult: 'These are not the products of water electrolysis.',
      explanation: 'Water (H₂O) splits into 2H₂ + O₂. Hydrogen appears at the cathode, oxygen at the anode.',
      points: 25
    },
    {
      id: 5,
      name: 'Combustion',
      question: 'What is needed for fire to burn (combustion)?',
      ingredients: ['Fuel', 'Oxygen', 'Water', 'Carbon Dioxide'],
      correctPair: ['Fuel', 'Oxygen'],
      result: 'Fire burns brightly! Heat and light are released.',
      wrongResult: 'Fire cannot start or continue without the right ingredients.',
      explanation: 'The fire triangle: Fuel + Oxygen + Heat = Combustion. Remove one and the fire goes out!',
      points: 25
    },
    {
      id: 6,
      name: 'States of Matter',
      question: 'What do you need to turn water into steam?',
      ingredients: ['Water', 'Heat', 'Cold', 'Pressure'],
      correctPair: ['Water', 'Heat'],
      result: 'The water boils and turns into steam (water vapour)!',
      wrongResult: 'This combination won\'t produce steam.',
      explanation: 'Adding heat energy makes water particles move faster until they escape as gas at 100°C.',
      points: 25
    },
    {
      id: 7,
      name: 'Making Salt',
      question: 'What do you mix to make table salt (sodium chloride)?',
      ingredients: ['Sodium', 'Chlorine', 'Oxygen', 'Carbon'],
      correctPair: ['Sodium', 'Chlorine'],
      result: 'Sodium chloride (table salt) crystals form!',
      wrongResult: 'These elements don\'t combine to make salt.',
      explanation: 'Sodium (Na) + Chlorine (Cl) = NaCl. This is an ionic bond - sodium gives an electron to chlorine!',
      points: 30
    },
    {
      id: 8,
      name: 'Elephant Toothpaste',
      question: 'What creates the foam in the elephant toothpaste experiment?',
      ingredients: ['Hydrogen Peroxide', 'Yeast', 'Vinegar', 'Oil'],
      correctPair: ['Hydrogen Peroxide', 'Yeast'],
      result: 'A huge foam eruption! Oxygen gas is released rapidly.',
      wrongResult: 'No foam reaction with these ingredients.',
      explanation: 'Yeast acts as a catalyst, rapidly breaking down hydrogen peroxide into water and oxygen gas!',
      points: 30
    },
    {
      id: 9,
      name: 'Density Tower',
      question: 'Which two liquids will separate with honey at the bottom?',
      ingredients: ['Honey', 'Water', 'Oil', 'Air'],
      correctPair: ['Honey', 'Oil'],
      result: 'The liquids separate into layers! Honey sinks, oil floats on water.',
      wrongResult: 'These don\'t create a clear density separation.',
      explanation: 'Denser liquids sink below less dense ones. Honey > Water > Oil in density!',
      points: 25
    },
    {
      id: 10,
      name: 'Invisible Ink',
      question: 'What can you use to write invisible messages that appear when heated?',
      ingredients: ['Lemon Juice', 'Heat', 'Blue Ink', 'Glue'],
      correctPair: ['Lemon Juice', 'Heat'],
      result: 'The invisible writing turns brown and becomes visible!',
      wrongResult: 'This combination doesn\'t create invisible ink.',
      explanation: 'Lemon juice oxidizes when heated, turning brown. The acid in the juice weakens the paper fibres!',
      points: 25
    },
    {
      id: 11,
      name: 'Magnetic Field',
      question: 'What do you need to see magnetic field lines?',
      ingredients: ['Magnet', 'Iron Filings', 'Sand', 'Water'],
      correctPair: ['Magnet', 'Iron Filings'],
      result: 'The iron filings arrange themselves along the magnetic field lines!',
      wrongResult: 'These materials won\'t show magnetic fields.',
      explanation: 'Iron is magnetic - the tiny filings align with the invisible magnetic field lines around the magnet!',
      points: 25
    },
    {
      id: 12,
      name: 'Static Electricity',
      question: 'What can you rub together to create static electricity?',
      ingredients: ['Balloon', 'Wool', 'Metal', 'Water'],
      correctPair: ['Balloon', 'Wool'],
      result: 'The balloon becomes charged and can stick to walls or attract hair!',
      wrongResult: 'These materials don\'t create static electricity effectively.',
      explanation: 'Rubbing transfers electrons from wool to the balloon, giving it a negative charge. Opposite charges attract!',
      points: 25
    }
  ];

  const currentExp = experiments[currentExperiment];

  const startGame = () => {
    setGameState('playing');
    setCurrentExperiment(0);
    setSelectedIngredients([]);
    setResult(null);
    setScore(0);
    setCompletedExperiments(0);
  };

  const toggleIngredient = (ingredient) => {
    if (result) return; // Don't allow changes after mix
    
    if (selectedIngredients.includes(ingredient)) {
      setSelectedIngredients(selectedIngredients.filter(i => i !== ingredient));
    } else if (selectedIngredients.length < 2) {
      setSelectedIngredients([...selectedIngredients, ingredient]);
    }
  };

  const mixIngredients = () => {
    if (selectedIngredients.length !== 2) {
      toast.error('Select exactly 2 ingredients to mix!');
      return;
    }

    const isCorrect = currentExp.correctPair.every(item => 
      selectedIngredients.includes(item)
    );

    if (isCorrect) {
      setResult({
        success: true,
        message: currentExp.result,
        explanation: currentExp.explanation
      });
      setScore(s => s + currentExp.points);
      setCompletedExperiments(c => c + 1);
    } else {
      setResult({
        success: false,
        message: currentExp.wrongResult,
        explanation: currentExp.explanation,
        correctAnswer: currentExp.correctPair.join(' + ')
      });
    }
  };

  const nextExperiment = () => {
    if (currentExperiment < experiments.length - 1) {
      setCurrentExperiment(c => c + 1);
      setSelectedIngredients([]);
      setResult(null);
    } else {
      endGame();
    }
  };

  const endGame = async () => {
    setGameState('finished');
    
    try {
      await api.post('/games/complete', {
        game_id: 'science-lab',
        score: score,
        metadata: { 
          experiments_completed: completedExperiments,
          total_experiments: experiments.length
        }
      });
      toast.success(`🎉 You earned ${score} points!`);
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  const getIngredientColor = (ingredient) => {
    if (selectedIngredients.includes(ingredient)) {
      return 'bg-teal-500 text-white border-teal-600';
    }
    return 'bg-white hover:bg-teal-50 border-slate-300';
  };

  return (
    <StudentLayout>
      <div data-testid="science-lab-game" className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
        </div>

        {/* Game Header */}
        <div className="student-card bg-teal-100 border-teal-300 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-teal-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
                <FlaskConical className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Science Lab</h1>
                <p className="text-slate-600">
                  {gameState === 'playing' 
                    ? `Experiment ${currentExperiment + 1} of ${experiments.length}` 
                    : 'Virtual experiments!'}
                </p>
              </div>
            </div>
            {gameState === 'playing' && (
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-500" />
                <span className="font-heading text-xl font-bold text-slate-800">{score}</span>
              </div>
            )}
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-teal-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <FlaskConical className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">Science Lab</h2>
            <p className="text-lg text-slate-600 mb-8">
              Conduct virtual experiments!<br/>
              Mix the right ingredients to discover scientific reactions.
            </p>
            <div className="flex justify-center gap-6 mb-8">
              <div className="text-center">
                <Beaker className="w-10 h-10 text-teal-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Mix chemicals</p>
              </div>
              <div className="text-center">
                <Atom className="w-10 h-10 text-purple-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Learn reactions</p>
              </div>
              <div className="text-center">
                <Sparkles className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Earn points</p>
              </div>
            </div>
            <Button onClick={startGame} className="student-btn text-lg px-10 py-6">
              Start Experiments
              <FlaskConical className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentExp && (
          <div className="student-card">
            {/* Experiment Name */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                <Beaker className="w-6 h-6 text-teal-600" />
              </div>
              <h2 className="font-heading text-xl font-bold text-slate-800">
                {currentExp.name}
              </h2>
            </div>

            {/* Question */}
            <div className="bg-teal-50 rounded-xl p-4 mb-6">
              <p className="text-lg text-slate-700 font-medium">{currentExp.question}</p>
            </div>

            {/* Ingredients */}
            <div className="mb-6">
              <p className="text-sm text-slate-500 mb-3">Select 2 ingredients to mix:</p>
              <div className="grid grid-cols-2 gap-3">
                {currentExp.ingredients.map((ingredient) => (
                  <button
                    key={ingredient}
                    onClick={() => toggleIngredient(ingredient)}
                    disabled={!!result}
                    className={`p-4 rounded-xl border-2 font-semibold transition-all ${getIngredientColor(ingredient)}`}
                    data-testid={`ingredient-${ingredient.toLowerCase().replace(' ', '-')}`}
                  >
                    {ingredient}
                  </button>
                ))}
              </div>
            </div>

            {/* Mix Button */}
            {!result && (
              <Button 
                onClick={mixIngredients}
                disabled={selectedIngredients.length !== 2}
                className="w-full student-btn mb-4"
                data-testid="mix-button"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Mix Ingredients!
              </Button>
            )}

            {/* Result */}
            {result && (
              <div className={`rounded-xl p-6 mb-6 ${result.success ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                <div className="flex items-start gap-3">
                  {result.success ? (
                    <Sparkles className="w-6 h-6 text-emerald-600 mt-1" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-amber-600 mt-1" />
                  )}
                  <div className="flex-1">
                    <p className={`font-bold text-lg mb-2 ${result.success ? 'text-emerald-800' : 'text-amber-800'}`}>
                      {result.success ? 'Success!' : 'Not quite right...'}
                    </p>
                    <p className={`mb-3 ${result.success ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {result.message}
                    </p>
                    
                    {/* Show correct answer when wrong */}
                    {!result.success && result.correctAnswer && (
                      <div className="bg-teal-100 border border-teal-300 rounded-lg p-3 mb-3">
                        <p className="text-teal-800 font-semibold">
                          Correct answer: <span className="text-teal-600">{result.correctAnswer}</span>
                        </p>
                      </div>
                    )}
                    
                    <div className="bg-white/50 rounded-lg p-3">
                      <p className="text-sm text-slate-700">
                        <strong>Science fact:</strong> {result.explanation}
                      </p>
                    </div>
                    {result.success && (
                      <p className="mt-3 text-emerald-700 font-bold">
                        +{currentExp.points} points!
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Next Button */}
            {result && (
              <Button onClick={nextExperiment} className="w-full student-btn">
                {currentExperiment < experiments.length - 1 ? 'Next Experiment' : 'See Results'}
              </Button>
            )}
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">Lab Complete!</h2>
            <p className="text-xl text-slate-600 mb-4">
              You completed {completedExperiments} out of {experiments.length} experiments!
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-600">+{score}</span>
              <span className="text-slate-600 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="student-btn">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default ScienceLabGame;
